// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../connections/AuthContext';
import routes_web from '../../connections/routes_web';
import { fetchHealthRecords, fetchHealthProfessionals } from './health_services';

// imported components
import TabHealthSummaryStats from './tab_summary/main_summary_stats';
import TabHealthSummaryAreas from './tab_summary/main_summary_areas';
import TabHealthRecords from './tab_records/main_records';
import TabHealthProfessionals from './tab_professionals/main_professionals';
import TabHealthSettings from './tab_settings/main_settings';
import MainHealthNotes from './tab_notes/main_notes';
import NavbarTop from './moduleComponents/NavbarTop';
import NavbarMiddle from './moduleComponents/NavbarMiddle';
import NavbarBottom from './moduleComponents/NavbarBottom';


// ###### MAIN COMPONENT
const HealthMain = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('summary');
  // L1 sub-tab, only meaningful within 'summary' for now — same role as
  // activeTabL1 in finance_main.js.
  const [activeTabL1, setActiveTabL1] = useState('areas');
  const [records, setRecords] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [isRecordsLoading, setIsRecordsLoading] = useState(true);


  const [filterType, setFilterType] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const types = [...new Set(records.map(o => o.type))].sort();
  const areas = [...new Set(records.map(o => o.area))].sort();

  useEffect(() => {
    loadHealthRecords();
    loadHealthProfessionals();
  }, []);

  // Reset L1 sub-tab whenever leaving/entering summary, mirrors finance's
  // "reset activeTabL1 on L2 change" effect.
  useEffect(() => {
    if (activeTab === 'summary') {
      setActiveTabL1('areas');
    }
  }, [activeTab]);

  // ### API REQUESTS
  const loadHealthRecords = async () => {
    setIsRecordsLoading(true);
    try {
      const response = await fetchHealthRecords();
      setRecords(response.data);
    } catch (err) {
      console.error("Error fetching health records:", err);
    } finally {
      setIsRecordsLoading(false);
    }
  };

  const loadHealthProfessionals = async () => {
    try {
      const response = await fetchHealthProfessionals();
      setProfessionals(response.data);
    } catch (err) {
      console.error("Error fetching health professionals:", err);
    }
  };

  // ### HANDLERS
  const handleTabChange = (tab) => setActiveTab(tab);

  const handleClearFilters = () => {
    setFilterType('');
    setFilterArea('');
    setFilterDescription('');
  };

  const showNavbarMiddle = activeTab === 'summary';

  // ### RETURN VIEW
  return (
    <div className="min-h-screen flex flex-col bg-gray-100 p-4 pb-24">
      {/* NavbarMiddle (L1) — only within summary for now, matches finance's
          "hide in settings" pattern inverted (show only where L1 exists) */}
      {showNavbarMiddle && (
        <NavbarMiddle activeTabL1={activeTabL1} setActiveTabL1={setActiveTabL1} />
      )}

      <div className={`max-w-5xl w-full mx-auto ${showNavbarMiddle ? 'pt-24' : 'pt-14'}`}>

        {/* Tabs Content */}
        {activeTab === 'summary' && activeTabL1 === 'stats' && (
          <TabHealthSummaryStats
            unfilteredRecords={records}
            isLoading={isRecordsLoading}
            filterType={filterType}
            setFilterType={setFilterType}
            filterArea={filterArea}
            setFilterArea={setFilterArea}
            filterDescription={filterDescription}
            setFilterDescription={setFilterDescription}
            types={types}
            areas={areas}
            handleClearFilters={handleClearFilters}
          />
        )}
        {activeTab === 'summary' && activeTabL1 === 'areas' && (
          <TabHealthSummaryAreas
            records={records}
            areas={areas}
            professionals={professionals}
            isLoading={isRecordsLoading}
          />
        )}
        {activeTab === 'records' && (
          <TabHealthRecords
            records={records}
            loadHealthRecords={loadHealthRecords}
            professionals={professionals}
            filterType={filterType}
            setFilterType={setFilterType}
            filterArea={filterArea}
            setFilterArea={setFilterArea}
            filterDescription={filterDescription}
            setFilterDescription={setFilterDescription}
            types={types}
            areas={areas}
            handleClearFilters={handleClearFilters}
          />
        )}
        {activeTab === 'professionals' && (
          <TabHealthProfessionals
            professionals={professionals}
            loadProfessionals={loadHealthProfessionals}
          />
        )}
        {activeTab === 'notes' && <MainHealthNotes />}
        {activeTab === 'settings' && (
          <TabHealthSettings
            records={records}
            loadHealthRecords={loadHealthRecords}
          />
        )}
      </div>

      {/* Navbar Top */}
      <NavbarTop
        title="Health"
        onHomeClick={() => navigate(routes_web.accounts_home)}
        onLogoutClick={logout}
        handleTabChange={handleTabChange}
        activeTab={activeTab}
      />

      {/* Navbar Bottom */}
      <NavbarBottom activeTab={activeTab} handleTabChange={handleTabChange} />

    </div>
  );
};

export default HealthMain;
